import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FiCheck, FiArrowRight } from 'react-icons/fi';
import { useFinalizePayment } from '../../services/api/subscription-api';
import Loading from '../../components/loading';

const Wrapper = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: var(--bg-main);
  color: var(--text-main);
  padding: 2rem;
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const checkPop = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.15);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const Card = styled.div`
  width: min(560px, 100%);
  background: var(--bg-card);
  border-radius: 18px;
  padding: 3rem 2.5rem;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(88, 166, 255, 0.08);
  text-align: center;
  animation: ${popIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 1.75rem;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #2ea04322, #56d36422);
  animation: ${checkPop} 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both;

  svg {
    width: 36px;
    height: 36px;
    color: #2ea043;
  }
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-main);
  opacity: 0.75;
  margin-bottom: 2rem;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.85rem 2.2rem;
  border: none;
  border-radius: 50px;
  color: #fff;
  background: linear-gradient(135deg, #58a6ff 0%, #79c0ff 100%);
  box-shadow: 0 4px 15px rgba(88, 166, 255, 0.25);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(88, 166, 255, 0.35);
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PurchaseSuccessPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const { mutate: finalizePayment } = useFinalizePayment();
  const [loading, setLoading] = useState(true);
  const paymentId = query.get('payment_id');
  const sessionId = query.get('session_id');
  const receivedPaymentId = paymentId || query.get('paymentId');

  useEffect(() => {
    if (!paymentId && !sessionId) {
      setLoading(false);
      return;
    }

    finalizePayment(
      { payment_id: receivedPaymentId, session_id: sessionId, status: 'success' },
      {
        onSettled: () => setLoading(false),
      }
    );
  }, [receivedPaymentId, sessionId, finalizePayment]);

  return (
    <Wrapper>
      <Card>
        {loading ? (
          <Loading />
        ) : (
          <>
            <IconWrapper>
              <FiCheck />
            </IconWrapper>
            <Title>Payment successful</Title>
            <Subtitle>Your purchase has been saved and the template is now unlocked.</Subtitle>
            <ActionButton onClick={() => navigate('/default/templates')}>
              Go to templates
              <FiArrowRight />
            </ActionButton>
          </>
        )}
      </Card>
    </Wrapper>
  );
}